import { buildSceneLayout } from '@/lib/transitions/buildSceneLayout';
import { sourceTimeToOutputTime } from '@/lib/transitions/mapTimes';
import type { SceneTransition } from '@/lib/transitions/types';
import type { ClientCall, ClientResult } from '../clientTools';

// Pure pieces of editor_capture_frames: which times to capture, which Player
// frame shows a source time, how big a frame may be, and keeping capture calls
// apart from the edits of the same batch (captures run after the edits).

export const CAPTURE_TOOL = 'editor_capture_frames';
export const MAX_CAPTURE_FRAMES = 4;
export const CAPTURE_MAX_SIDE = 720;
export const CAPTURE_JPEG_QUALITY = 0.8;

const roundTenth = (value: number) => Math.round(value * 10) / 10;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Clip-timeline seconds to capture: clamped to the clip, rounded to 0.1 s,
 *  deduplicated, at most 4 in the order given. Nothing usable → the playhead. */
export function normalizeCaptureTimes(
  raw: unknown,
  { playhead, duration }: { playhead: number; duration: number },
): number[] {
  const end = Math.max(0, Number.isFinite(duration) ? duration : 0);
  const times: number[] = [];
  (Array.isArray(raw) ? raw : []).forEach((value) => {
    if (times.length >= MAX_CAPTURE_FRAMES) return;
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    const time = roundTenth(clamp(value, 0, end));
    if (!times.includes(time)) times.push(time);
  });
  if (times.length) return times;
  const fallback = Number.isFinite(playhead) ? playhead : 0;
  return [roundTenth(clamp(fallback, 0, end))];
}

interface PlayerFrameInput {
  sourceTime: number;
  /** The Player shows the kept scenes (playing or navigation-locked). */
  locked: boolean;
  intervals: ReadonlyArray<{ startTime: number; endTime: number }>;
  transitions: SceneTransition[];
  fps: number;
  /** Full source duration (unlocked axis). */
  fullDuration: number;
}

/** The Player frame that shows a source-video time — the same mapping the
 *  editor's seek uses (overlap-aware when locked). */
export function resolvePlayerFrame({
  sourceTime, locked, intervals, transitions, fps, fullDuration,
}: PlayerFrameInput): number {
  if (!locked) {
    return Math.round(clamp(sourceTime, 0, Math.max(0, fullDuration || 0)) * fps);
  }
  if (!intervals.length) return 0;
  const minStart = Math.min(...intervals.map((interval) => interval.startTime));
  const maxEnd = Math.max(...intervals.map((interval) => interval.endTime));
  const clamped = clamp(sourceTime, minStart, maxEnd);
  const scenes = intervals.map((interval) => ({ ...interval }));
  const { blocks } = buildSceneLayout({ scenes, transitions, fps });
  return Math.round(sourceTimeToOutputTime(scenes, blocks, clamped, fps) * fps);
}

/** Scales down (never up) so the long side fits `maxSide`. */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const longSide = Math.max(width, height);
  if (longSide <= maxSide || longSide <= 0) return { width, height };
  const scale = maxSide / longSide;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export function splitCaptureCalls(calls: readonly ClientCall[]): { capture: ClientCall[]; edits: ClientCall[] } {
  return {
    capture: calls.filter((call) => call.tool_name === CAPTURE_TOOL),
    edits: calls.filter((call) => call.tool_name !== CAPTURE_TOOL),
  };
}

/** Results back in the order the model made the calls. */
export function orderResults(calls: readonly ClientCall[], results: readonly ClientResult[]): ClientResult[] {
  const byId = new Map(results.map((result) => [result.id, result]));
  return calls.map((call) => byId.get(call.id)).filter((result): result is ClientResult => !!result);
}

/** A frame's name in the chat: its clip time as m:ss. */
export function formatFrameClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
